import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseResponse } from 'src/app/core/models/responses/base-response';
import { environment } from 'src/environments/environment';
import { ApplicationUser } from '../../models/application-user/application-user';
import { ApplicationUserFilter } from '../../models/filters/application-user-filter';
import { BaseService } from './base.service';
import { Router } from '@angular/router';
import { Branch } from 'src/app/models/branch/branch';
import { LatestUsageModel } from 'src/app/models/application-user/latest-usage-model';
import { CustomJwtTokenResponse } from '../models/responses/custom-jwt-token-response';
import { WidgetService } from 'src/app/services/widget.service';
import { Role } from '../models/role.enum';
import { BaseListResponse } from '../models/responses/base-list-response';
import { SignalRService } from 'src/app/services/signalr.service';
import { AlertService } from 'src/app/services/alert.service';
import { MarketplaceNotificationHubService } from 'src/app/services/marketplace-notification-hub.service';
import { EInvoiceStatusHubService } from 'src/app/services/e-invoice-status-hub.service';
import { MasakHubService } from 'src/app/services/masak-hub.service';
import { PosHubService } from 'src/app/services/pos-hub.service';
import { MenuItem } from 'src/app/models/menu/menu.model';
@Injectable({
  providedIn: 'root'
})
export class ApplicationUserService extends BaseService<ApplicationUser, ApplicationUserFilter, string> {

  apiUrl = environment.urlAddress;
  public currentUserSubject: BehaviorSubject<ApplicationUser>;
  public latestUsageSubject: BehaviorSubject<LatestUsageModel>;
  public currentBranchSubject: BehaviorSubject<Branch>;
  public currentUser: Observable<ApplicationUser>;
  public currentBranch: Observable<Branch>;


  constructor(private http: HttpClient, private router: Router, private widgetService: WidgetService, private signalRService: SignalRService, private alertService: AlertService, private marketplaceNotificationHubService: MarketplaceNotificationHubService, private eInvoiceStatusHub: EInvoiceStatusHubService, private masakHub: MasakHubService, private posHub: PosHubService) {
    super(http, environment.urlAddress + '/applicationUser');
    const storedUser = this.normalizeCurrentUser(JSON.parse(localStorage.getItem('currentUser')));
    if (storedUser) {
      localStorage.setItem('currentUser', JSON.stringify(storedUser));
    }
    this.currentUserSubject = new BehaviorSubject<ApplicationUser>(storedUser);
    this.currentBranchSubject = new BehaviorSubject<Branch>(JSON.parse(localStorage.getItem('currentBranch')));
    this.currentUser = this.currentUserSubject.asObservable();
    this.currentBranch = this.currentBranchSubject.asObservable();

    // Sayfa yenilenmesinde oturum açık kalırsa hub'ı yeniden bağla
    const existingUser = this.currentUserSubject.value;
    const existingBranch = this.currentBranchSubject.value;
    if (existingUser?.token && existingBranch?.id) {
      this.marketplaceNotificationHubService
        .connect(existingUser.token, String(existingBranch.id))
        .catch(() => {});
      this.connectBankAccountHub();
      // MASAK yaptırım taraması bildirimleri (yalnızca MASAK ekranı yetkisi olana bağlanır).
      this.masakHub.connect(existingUser);
      // POS işlem sonuçları (yalnızca POS İşlemleri ekranı yetkisi olana bağlanır).
      this.posHub.connect(existingUser);
    }
  }

  // Banka hareketi bildiriminin TEK sahibi burasıdır. Dinleyici hem burada hem
  // AppComponent'te kayıtlıydı; aynı bağlantıya iki handler bağlanınca her hareket
  // için iki toast çıkıyordu. Bayrak sayesinde kaç kez çağrıldığı önemsiz.
  private bankAccountHubRegistered = false;

  connectBankAccountHub(): void {
    if (this.bankAccountHubRegistered) {
      return;
    }

    const connection = this.signalRService.start('/bankAccountHub');
    if (!connection) {
      // Token yoksa start() null döner; login sonrası tekrar denenecek.
      return;
    }

    this.bankAccountHubRegistered = true;
    connection.on('receiveTransactionReceived', (data) => {
      if (!data?.bankName) {
        return;
      }

      const title = `<img src='${data.bankLogo}' title='${data.bankName}' alt='${data.bankName}' style='width: 100px;' />`;

      // Sunucu yerel hareket kimliğini (BankTransactionNotification.Id) gönderiyor.
      //
      // Tam sayı kontrolü ŞART: eski sunucu sağlayıcının ham kaydını itiyordu ve
      // ONUN da `id` alanı var — ama Guid. Ön yüz sunucudan önce yayına alınırsa
      // o Guid ile rota kurulur ve kullanıcı hataya düşerdi. Kimlik yoksa ya da
      // sayı değilse bildirim tıklanamaz kalır; eski davranış korunur.
      const transactionId = Number(data.id);
      const canOpenDetail = Number.isInteger(transactionId) && transactionId > 0;

      if (!canOpenDetail) {
        this.alertService.showSuccessToastHtml(title, this.buildBankTransactionToastBody(data, false));
        return;
      }

      this.alertService.showClickableSuccessToastHtml(
        title,
        this.buildBankTransactionToastBody(data, true),
        () => this.router.navigate(['/specifications/bank-account-transaction/detail', transactionId]));
    });
  }

  private buildBankTransactionToastBody(data: any, clickable: boolean): string {
    const hint = clickable
      ? `<div class='mt-1 fs-6 text-primary'><i class='mdi mdi-open-in-new me-1'></i>Detay için tıklayın</div>`
      : '';

    return `<div title='${data.description}' class='mb-0'>
           <div class='fw-bold'>Tutar: ${data.amountString}</div>
           <div class='mt-1 fs-6 text-gray'>${data.description}</div>
           ${hint}
           <div class='mt-1 fs-6 text-gray text-end align-items-center d-flex gap-2 float-end'><i class='text-info fa fa-clock'></i> ${data.transactionDate}</div>
         </div>`;
  }


  login(username: string, password: string): Observable<BaseResponse<ApplicationUser>>{

    return this.http.post<BaseResponse<ApplicationUser>>(this.apiUrl + '/applicationUser/login', {username, password})
      .pipe(map(response => {
          const normalizedUser = this.normalizeCurrentUser(response?.data);
          if (response) {
            response.data = normalizedUser;
          }

          if(normalizedUser && normalizedUser.token && normalizedUser.branchList?.length > 0){
            let parities = localStorage.getItem('parities');
            if (parities == undefined)
              localStorage.setItem('parities', JSON.stringify([]));
            localStorage.setItem('subSystemName', normalizedUser.subSystemName || '');
            this.persistCurrentUser(normalizedUser);
            this.widgetService.widgetsSubject.next(normalizedUser.widgetList);
            if(normalizedUser.branchList.length > 0){
              localStorage.setItem('currentBranch', JSON.stringify(normalizedUser.branchList[0]));
              this.currentBranchSubject.next(normalizedUser.branchList[0]);
            }
            /*
            let connection = this.signalRService.start('/parityHub', response.data.token);
            connection.on('receiveParityAddedMessage', (data) => {
              console.log('📥 Login sonrası parity güncellemesi:', data);
            });
*/
         
            this.connectBankAccountHub();
            this.masakHub.connect(normalizedUser);
            this.posHub.connect(normalizedUser);

            // Marketplace bildirim hub'ını global olarak bağla
            const branchId = this.currentBranchSubject.value?.id;
            if (branchId) {
              this.marketplaceNotificationHubService
                .connect(normalizedUser.token, String(branchId))
                .catch(() => {});
            }
          }
        return response;
      }))
  }

  logout() {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentBranch');
    this.signalRService.stop('/parityHub');
    this.signalRService.stop('/bankAccountHub');
    // Bağlantı kapandı; bir sonraki oturumda dinleyici yeniden kurulmalı.
    this.bankAccountHubRegistered = false;
    // E-belge durum akışı ekranlarda açılıyor ama kapatması burada: oturum
    // kapanınca soket de kapanmalı, bir sonraki kullanıcının belgelerini
    // önceki oturumun bağlantısı dinlemesin.
    this.eInvoiceStatusHub.disconnect();
    this.masakHub.disconnect();
    this.posHub.disconnect();
    this.marketplaceNotificationHubService.disconnect().catch(() => {});
    this.currentUserSubject.next(null);
  }

  public get currentUserValue(): ApplicationUser {
    return this.currentUserSubject.value;
}

public get currentBranchValue(): Branch {
  return this.currentBranchSubject.value;
}

  setHasEcommerce(value: boolean): void {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser) {
      return;
    }

    const normalizedUser = this.normalizeCurrentUser({
      ...currentUser,
      hasEcommerce: value
    });

    this.persistCurrentUser(normalizedUser);
  }


  async addBranch(branch: Branch){
    // var currentUser: ApplicationUser = JSON.parse(localStorage.getItem("currentUser"));
    // currentUser.branchList.push(branch);
    // this.currentUserSubject.next(currentUser);
    // localStorage.setItem("currentUser", JSON.stringify(currentUser));
  }

  async deleteBranch(id: number){
    var currentUser: ApplicationUser = JSON.parse(localStorage.getItem("currentUser"));
    currentUser.branchList = currentUser.branchList.filter(p=> p.id !== id);
    this.currentUserSubject.next(currentUser);
    localStorage.setItem("currentUser", JSON.stringify(currentUser));
  }

  async branchListChanged(newList: Branch[]){
    var currentUser: ApplicationUser = JSON.parse(localStorage.getItem("currentUser"));
    currentUser.branchList = newList;
    this.currentUserSubject.next(currentUser);
    localStorage.setItem("currentUser", JSON.stringify(currentUser));
  }

  changeBranch(branch: Branch){
    this.currentBranchSubject.next(branch);
    localStorage.setItem("currentBranch", JSON.stringify(branch))
  }

  update(user: ApplicationUser): Observable<BaseResponse<ApplicationUser>>{
    return this.http.put<BaseResponse<ApplicationUser>>(this.apiUrl + '/applicationUser', user)
  }

  updateLatestUsage(values: any) {
    this.latestUsageSubject.next(values);
  }

  refreshToken(token: string): Observable<BaseResponse<CustomJwtTokenResponse>>{
    return this.http.post<BaseResponse<CustomJwtTokenResponse>>(this.apiUrl + '/applicationUser/refresh-token', {token: token});
  }

  availableRoles(): Observable<BaseListResponse<Role>>{
    return this.http.post<BaseListResponse<Role>>(this.apiUrl + '/applicationUser/available-roles', null);
  }

  private persistCurrentUser(user: ApplicationUser | null): void {
    if (user) {
      localStorage.setItem('currentUser', JSON.stringify(user));
    }

    this.currentUserSubject.next(user);
  }

  private normalizeCurrentUser(user: ApplicationUser | null): ApplicationUser | null {
    if (!user) {
      return user;
    }

    return {
      ...user,
      hasEcommerce: user.hasEcommerce === true,
      hasRfid: user.hasRfid === true,
      menuItemList: this.normalizeMenuItems(user.menuItemList, user.hasEcommerce === true)
    };
  }

  private normalizeMenuItems(menuItems: MenuItem[] | undefined, hasEcommerce: boolean): MenuItem[] {
    if (!menuItems?.length) {
      return [];
    }

    return menuItems.map((item) => {
      const normalizedItem: MenuItem = {
        ...item,
        childrenMenuItem: (item.childrenMenuItem || []).map((child) => ({
          ...child,
          childrenMenuItem: child.childrenMenuItem ? [...child.childrenMenuItem] : []
        }))
      };

      if (this.isSanalMagazamMenu(normalizedItem)) {
        normalizedItem.childrenMenuItem = hasEcommerce
          ? normalizedItem.childrenMenuItem
          : [this.buildCreateStoreMenuItem(normalizedItem)];
      }

      return normalizedItem;
    });
  }

  private isSanalMagazamMenu(item: MenuItem): boolean {
    const code = (item.code || '').toLowerCase();
    const hasEcommerceChildren = (item.childrenMenuItem || []).some((child) =>
      (child.link || '').startsWith('ecommerce-admin/')
    );
    return code === 'sanal-magazam' || hasEcommerceChildren;
  }

  private buildCreateStoreMenuItem(parent: MenuItem): MenuItem {
    return {
      id: 1311,
      code: 'ecommerce-admin-create-store',
      label: 'MAĞAZA OLUŞTUR',
      link: 'ecommerce-admin/create-store',
      isActive: true,
      parentId: parent.id,
      parentMenuItemId: parent.id
    };
  }

}

